import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  ArrowLeft,
  Award,
  Target,
  Users,
  TrendingUp,
  CheckCircle,
  Lightbulb,
  MessageSquare,
  Rocket
} from 'lucide-react';
import PageHeader from '../components/PageHeader';

const CalidadPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-white/60">
      <PageHeader hideCTA={true} />

      {/* Contenido Principal */}
      <main className="pt-32 pb-20 px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
          
          {/* Botón Volver */}
          <button
            type="button"
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-[#141414] mb-8 transition font-medium"
          >
            <ArrowLeft size={16} />
            {t('common.back')}
          </button>

          {/* Hero de la Página */}
          <div className="text-center mb-16">
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <Award className="text-green-600" size={32} />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-[#141414] mb-4">
              {t('calidad.title')}
            </h1>
            <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
              {t('calidad.subtitle')}
            </p>
          </div>

          {/* Información de la Política */}
          <div className="bg-gray-50 rounded-lg p-6 mb-12">
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle className="text-green-600" size={24} />
              <h2 className="text-xl font-semibold text-[#141414]">{t('calidad.statement_title')}</h2>
            </div>
            <div className="text-sm text-gray-600 mb-4">
              <span className="font-medium">{t('privacidad.review_date')}:</span> 06/10/2023 | 
              <span className="font-medium ml-2">{t('privacidad.approval_date')}:</span> 10/10/2023
            </div>
          </div>

          {/* Descripción Principal */}
          <div className="mb-12">
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              {t('calidad.main_desc')}
            </p>
          </div>

          {/* Compromisos */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-[#141414] mb-8 text-center">{t('calidad.commitments_title')}</h2>
            
            <div className="grid gap-6">
              
              {/* Compromiso 1 */}
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Target className="text-blue-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#141414] mb-2">{t('calidad.items.compliance.title')}</h3>
                    <p className="text-gray-600">
                      {t('calidad.items.compliance.desc')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Compromiso 2 */}
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="text-green-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#141414] mb-2">{t('calidad.items.improvement.title')}</h3>
                    <p className="text-gray-600">
                      {t('calidad.items.improvement.desc')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Compromiso 3 */}
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Users className="text-purple-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#141414] mb-2">{t('calidad.items.team.title')}</h3>
                    <p className="text-gray-600">
                      {t('calidad.items.team.desc')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Compromiso 4 */}
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Award className="text-yellow-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#141414] mb-2">{t('calidad.items.satisfaction.title')}</h3>
                    <p className="text-gray-600">
                      {t('calidad.items.satisfaction.desc')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Compromiso 5 */}
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Lightbulb className="text-indigo-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#141414] mb-2">{t('calidad.items.innovation.title')}</h3>
                    <p className="text-gray-600">
                      {t('calidad.items.innovation.desc')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Compromiso 6 */}
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <MessageSquare className="text-teal-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#141414] mb-2">{t('calidad.items.communication.title')}</h3>
                    <p className="text-gray-600">
                      {t('calidad.items.communication.desc')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Compromiso 7 */}
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Rocket className="text-red-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#141414] mb-2">{t('calidad.items.performance.title')}</h3>
                    <p className="text-gray-600">
                      {t('calidad.items.performance.desc')}
                    </p>
                  </div>
                </div>
              </div>

            </div>
          </div>

        </div>
      </main>
    </div>
  );
};

export default CalidadPage;
